import { Alert, SimpleGrid, Stack, Switch, TagsInput, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconClock } from '@tabler/icons-react';
import { formatDateTime, isFuture, toIso } from '@/lib/format';
import { Section } from './DetailsTab';
import { useProgrammeFormContext } from './formContext';

const TERRITORY_SUGGESTIONS = ['GB', 'IE', 'US', 'CA', 'AU', 'NZ', 'WW'];

export function RightsTab() {
  const form = useProgrammeFormContext();
  const v = form.getValues();
  const dt = { valueFormat: 'D MMM YYYY HH:mm', clearable: true, placeholder: 'Not set' } as const;

  return (
    <Stack gap="lg">
      <Section
        title="Rights"
        description="Captured for reporting. Only the availability window is enforced in the app for now."
      >
        <TagsInput
          label="Territories"
          description="ISO country codes, e.g. GB. At least one is required to approve."
          data={TERRITORY_SUGGESTIONS}
          required
          {...form.getInputProps('territories')}
          onChange={(vals) =>
            form.setFieldValue(
              'territories',
              vals.map((x) => x.trim().toUpperCase()).filter(Boolean),
            )
          }
        />
        <SimpleGrid cols={{ base: 1, xs: 2 }}>
          <Switch
            label="Streaming allowed"
            {...form.getInputProps('rights.stream', { type: 'checkbox' })}
          />
          <Switch
            label="Downloads allowed"
            {...form.getInputProps('rights.download', { type: 'checkbox' })}
          />
        </SimpleGrid>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <DateTimePicker
            label="Available from"
            description="Licence window start."
            {...dt}
            {...form.getInputProps('available_from')}
          />
          <DateTimePicker
            label="Available to"
            description="Licence window end. Must be after the start."
            {...dt}
            {...form.getInputProps('available_to')}
          />
        </SimpleGrid>
      </Section>

      <Section
        title="Schedule"
        description="Optional. The programme still needs to be approved and published; these dates control when children can see it."
      >
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <DateTimePicker
            label="Publish at"
            description="Leave empty to go live as soon as it’s published."
            {...dt}
            {...form.getInputProps('publish_at')}
          />
          <DateTimePicker
            label="Unpublish at"
            description="Comes down automatically at this time."
            {...dt}
            {...form.getInputProps('unpublish_at')}
          />
        </SimpleGrid>
        {(isFuture(toIso(v.publish_at)) || v.unpublish_at) && (
          <Alert color="lilac" variant="light" icon={<IconClock size={18} />}>
            <Text size="sm">
              {isFuture(toIso(v.publish_at)) &&
                `Once published, children see it from ${formatDateTime(toIso(v.publish_at))}. `}
              {v.unpublish_at &&
                `It stops being shown at ${formatDateTime(toIso(v.unpublish_at))}.`}
            </Text>
          </Alert>
        )}
      </Section>
    </Stack>
  );
}
